import { useEffect, useRef, type CSSProperties } from 'react';
import { CompanionProfile, getCompanionDisplayName, isPresetCompanion } from '@/lib/useCompanions';
import styles from './PetCompanionPicker.module.less';

type Props = {
  open: boolean;
  disabled: boolean;
  profiles: CompanionProfile[];
  activeId?: string;
  canAddMore: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (id: string) => void;
  onAdd: () => void;
  onDelete: (id: string) => void;
};

function Portrait({ profile }: { profile: CompanionProfile }) {
  const name = getCompanionDisplayName(profile);
  return profile.avatar ? (
    <img src={profile.avatar} alt="" />
  ) : (
    <span>{name.trim().slice(0, 1).toUpperCase()}</span>
  );
}

export default function PetCompanionPicker({
  open,
  disabled,
  profiles,
  activeId,
  canAddMore,
  onOpenChange,
  onSelect,
  onAdd,
  onDelete,
}: Props) {
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const previews = [
    ...profiles.filter((profile) => profile.id === activeId),
    ...profiles.filter((profile) => profile.id !== activeId),
  ].slice(0, 3);

  useEffect(() => {
    if (!open || disabled) return;
    const panel = panelRef.current;
    (
      panel?.querySelector<HTMLButtonElement>('[aria-pressed="true"]') ??
      panel?.querySelector<HTMLButtonElement>('button')
    )?.focus();
    const closeOutside = (event: Event) => {
      if (!rootRef.current?.contains(event.target as Node)) onOpenChange(false);
    };
    const closeEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      onOpenChange(false);
      triggerRef.current?.focus();
    };
    document.addEventListener('pointerdown', closeOutside);
    document.addEventListener('click', closeOutside);
    document.addEventListener('keydown', closeEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOutside);
      document.removeEventListener('click', closeOutside);
      document.removeEventListener('keydown', closeEscape);
    };
  }, [open, disabled, onOpenChange]);

  const choose = (id: string) => {
    onSelect(id);
    onOpenChange(false);
    triggerRef.current?.focus();
  };

  return (
    <div ref={rootRef} className={styles.root}>
      <button
        ref={triggerRef}
        type="button"
        className={`${styles.trigger} ${open ? styles.triggerOpen : ''}`}
        disabled={disabled}
        aria-label="换个伙伴"
        aria-expanded={open && !disabled}
        aria-controls="desktop-companion-picker"
        aria-haspopup="dialog"
        onClick={() => onOpenChange(!open)}
      >
        <span className={styles.preview} aria-hidden="true">
          {previews.map((profile) => (
            <span key={profile.id}>
              <Portrait profile={profile} />
            </span>
          ))}
        </span>
        <span>换伙伴</span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M4 8h15m-4-4 4 4-4 4M20 16H5m4-4-4 4 4 4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && !disabled && (
        <div
          ref={panelRef}
          id="desktop-companion-picker"
          role="dialog"
          aria-label="选择陪伴你的伙伴"
          className={styles.panel}
        >
          <div className={styles.heading}>
            <div>
              <span>下一位，谁来陪你？</span>
              <small>{profiles.length} 位伙伴 · 随时相见</small>
            </div>
            <button
              type="button"
              aria-label="收起伙伴"
              onClick={() => {
                onOpenChange(false);
                triggerRef.current?.focus();
              }}
            >
              ×
            </button>
          </div>
          <div className={styles.grid}>
            {profiles.map((profile, index) => {
              const active = profile.id === activeId;
              const name = getCompanionDisplayName(profile);
              return (
                <div
                  key={profile.id}
                  className={`${styles.item} ${active ? styles.active : ''}`}
                  style={{ '--delay': `${index * 18}ms` } as CSSProperties}
                >
                  <button
                    type="button"
                    className={styles.choice}
                    aria-label={`选择${name}`}
                    aria-pressed={active}
                    onClick={() => choose(profile.id)}
                  >
                    <span className={styles.portrait}>
                      <Portrait profile={profile} />
                      {active && <i>✓</i>}
                    </span>
                    <span className={styles.name}>{name}</span>
                    <small>{active ? '正在陪你' : '点击召唤'}</small>
                  </button>
                  {!isPresetCompanion(profile) && (
                    <button
                      type="button"
                      className={styles.delete}
                      aria-label={`删除${name}`}
                      onClick={() => onDelete(profile.id)}
                    >
                      ×
                    </button>
                  )}
                </div>
              );
            })}
            {canAddMore && (
              <div className={styles.item}>
                <button
                  type="button"
                  className={`${styles.choice} ${styles.add}`}
                  onClick={() => {
                    onOpenChange(false);
                    onAdd();
                  }}
                >
                  <span className={styles.portrait}>+</span>
                  <span className={styles.name}>新伙伴</span>
                  <small>创造新相遇</small>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
